// IMPORTS

// external
import algosdk, {
  Algodv2,
  Transaction,
  getApplicationAddress,
  encodeUint64,
  decodeUint64,
  SuggestedParams,
  assignGroupID,
  bytesToBigInt
} from "algosdk"

// global
import {
  FIXED_3_SCALE_FACTOR,
  FIXED_6_SCALE_FACTOR,
  FIXED_18_SCALE_FACTOR,
  ALGO_ASSET_ID,
  SECONDS_PER_YEAR,
  PERMISSIONLESS_SENDER_LOGIC_SIG,
  TEXT_ENCODER
} from "../globals"
import { Base64Encoder } from "../encoder"
import { decodeBytes, parseAddressBytes, composeTransactions } from "../utils"
import { getApplicationGlobalState, getLocalStates, getAccountBalances } from "../stateUtils"
import { getParams, getPaymentTxn } from "../transactionUtils"
import AlgofiClient from "../algofiClient"
import AlgofiUser from "../algofiUser"
import { roundUp, roundDown } from "../utils"

// assetData
import AssetAmount from "../assetData/assetAmount"
import AssetDataClient from "../assetData/assetDataClient"

// lending
import Market from "../lending/v2/market"

// amm
import Pool, { PoolQuote, PoolQuoteType } from "../amm/v1/pool"
import { PoolType } from "../amm/v1/ammConfig"

// local
import LendingPoolRouterInterfaceConfig, { LENDING_POOL_ROUTER_INTERFACE_STRINGS } from "./lendingPoolRouterInterfaceConfig"

// CONSTANTS

const IS_PROJECTED: boolean = true

// HELPER CLASSES

// INTERFACE

export default class LendingPoolInterface {
  // static
  public algofiClient: AlgofiClient
  public algod: Algodv2
  public appId: number
  public asset1Id: number
  public intermediatebAssetId: number
  public asset2Id: number
  public market1AppId: number
  public market2AppId: number
  public pool1AppId: number
  public pool2AppId: number
  public opFarmAppId: number
  
  // derived
  public address: string

  // classes
  public market1: Market
  public market2: Market
  public pool1: Pool
  public pool2: Pool

  constructor(algofiClient: AlgofiClient, config: LendingPoolRouterInterfaceConfig) {
    this.algofiClient = algofiClient
    this.algod = this.algofiClient.algod
    this.appId = config.appId
    this.asset1Id = config.asset1Id
    this.intermediatebAssetId = config.intermediatebAssetId
    this.asset2Id = config.asset2Id
    this.market1AppId = config.market1AppId
    this.market2AppId = config.market2AppId
    this.pool1AppId = config.pool1AppId
    this.pool2AppId = config.pool2AppId
    this.opFarmAppId = config.opFarmAppId
    
    this.address = getApplicationAddress(this.appId)
  }
  
  async loadState() {
    this.market1 = this.algofiClient.lending.v2.markets[this.market1AppId]
    this.market2 = this.algofiClient.lending.v2.markets[this.market2AppId]
    this.pool1 = await this.algofiClient.amm.v1.getPoolByAppId(this.pool1AppId)
    this.pool2 = await this.algofiClient.amm.v1.getPoolByAppId(this.pool2AppId)
  }
  
  // GETTERS

  // QUOTES

  getSwapExactForQuote(swapInAssetId: number, swapInAmount: number): PoolQuote {
    let asset1SwapAmount = 0
    let bAsset1SwapAmount = 0
    let intermediatebAssetSwapAmount = 0
    let asset2SwapAmount = 0
    let bAsset2SwapAmount = 0
    let numIter = 0
    
    let swapInAssetAmount = this.algofiClient.assetData.getAsset(swapInAmount, swapInAssetId)
    
    if (swapInAssetId == this.market1.underlyingAssetId) {
      asset1SwapAmount = -1 * swapInAmount
      bAsset1SwapAmount = this.market1.underlyingToBAsset(swapInAssetAmount).amount
      let pool1Quote = this.pool1.getSwapExactForQuote(this.market1.bAssetId, bAsset1SwapAmount)
      intermediatebAssetSwapAmount = this.pool1.asset1Id == this.intermediatebAssetId ? pool1Quote.asset1Delta : pool1Quote.asset2Delta
      let pool2Quote = this.pool2.getSwapExactForQuote(this.intermediatebAssetId, intermediatebAssetSwapAmount)
      bAsset2SwapAmount = this.pool2.asset1Id == this.market2.bAssetId ? pool2Quote.asset1Delta : pool2Quote.asset2Delta
      asset2SwapAmount = this.market2.bAssetToUnderlying(bAsset2SwapAmount).amount
      numIter = pool1Quote.iterations + pool2Quote.iterations
    } else {
      asset2SwapAmount = -1 * swapInAmount
      bAsset2SwapAmount = this.market2.underlyingToBAsset(swapInAssetAmount).amount
      let pool2Quote = this.pool2.getSwapExactForQuote(this.market2.bAssetId, bAsset2SwapAmount)
      intermediatebAssetSwapAmount = this.pool2.asset1Id == this.intermediatebAssetId ? pool2Quote.asset1Delta : pool2Quote.asset2Delta
      let pool1Quote = this.pool1.getSwapExactForQuote(this.intermediatebAssetId, intermediatebAssetSwapAmount)
      bAsset1SwapAmount = this.pool1.asset1Id == this.market1.bAssetId ? pool1Quote.asset1Delta : pool1Quote.asset2Delta
      asset1SwapAmount = this.market1.bAssetToUnderlying(bAsset1SwapAmount).amount
      numIter = pool2Quote.iterations + pool1Quote.iterations
    }
    
    return new PoolQuote(PoolQuoteType.SWAP_EXACT_FOR, asset1SwapAmount, asset2SwapAmount, 0, numIter)
  }
  
  // TRANSACTION GETTERS

  async getSwapTxns(
    user: AlgofiUser,
    quote: PoolQuote,
    maxSlippage: number = 0.005
  ): Promise<Transaction[]> {
    const params  = await getParams(this.algod)
    const transactions = []
    
    let additionalFees = 30000 + quote.iterations * 1000 

    // OPT IN TO ASSET1 (optional)
    if (!user.isOptedInToAsset(this.market1.underlyingAssetId)) {
      transactions.push(getPaymentTxn(params, user.address, user.address, this.market1.underlyingAssetId, 0))
    }
    
    // OPT IN TO ASSET2 (optional)
    if (!user.isOptedInToAsset(this.market2.underlyingAssetId)) {
      transactions.push(getPaymentTxn(params, user.address, user.address, this.market2.underlyingAssetId, 0))
    }
    
    let inputIsAsset1 = (quote.asset1Delta < 0)
    let inputAsset = inputIsAsset1 ? this.market1.underlyingAssetId : this.market2.underlyingAssetId
    let inputAmount = inputIsAsset1 ? -1 * quote.asset1Delta : -1 * quote.asset2Delta
    let minBAssetOutputAmount = inputIsAsset1 ? 
      this.market2.underlyingToBAsset(this.algofiClient.assetData.getAsset(quote.asset2Delta, this.market2.underlyingAssetId)).amount :
      this.market1.underlyingToBAsset(this.algofiClient.assetData.getAsset(quote.asset1Delta, this.market1.underlyingAssetId)).amount
    
    if (quote.quoteType == PoolQuoteType.SWAP_EXACT_FOR) {
      minBAssetOutputAmount = Math.floor(minBAssetOutputAmount * (1 - maxSlippage))
    } else {
      inputAmount = Math.ceil(inputAmount * (1 + maxSlippage))
    }
    
    // SEND ASSET
    transactions.push(getPaymentTxn(params, user.address, this.address, inputAsset, inputAmount))
    
    // SWAP STEP 1 (mint)
    params.fee = 1000 + additionalFees
    transactions.push(
      algosdk.makeApplicationNoOpTxnFromObject({
        from: user.address,
        appIndex: this.appId,
        suggestedParams: params,
        appArgs: [
          TEXT_ENCODER.encode(LENDING_POOL_ROUTER_INTERFACE_STRINGS.swap_step_1),
          encodeUint64(quote.iterations),
          encodeUint64(inputAsset)
        ],
        accounts: [],
        foreignApps: [this.opFarmAppId, this.market1AppId, this.market2AppId, this.market1.managerAppId],
        foreignAssets: [this.market1.underlyingAssetId, this.market1.bAssetId, this.market2.underlyingAssetId, this.market2.bAssetId],
        rekeyTo: undefined
      })
    )
    
    // SWAP STEP 2 (swap)
    params.fee = 0
    transactions.push(
      algosdk.makeApplicationNoOpTxnFromObject({
        from: user.address,
        appIndex: this.appId,
        suggestedParams: params,
        appArgs: [
          TEXT_ENCODER.encode(LENDING_POOL_ROUTER_INTERFACE_STRINGS.swap_step_2),
          encodeUint64(inputAsset),
          encodeUint64(minBAssetOutputAmount)
        ],
        accounts: [],
        foreignApps: [this.pool1AppId, this.pool2AppId, this.pool1.managerAppId],
        foreignAssets: [this.pool1.asset1Id, this.pool1.asset2Id, this.pool2.asset1Id, this.pool2.asset2Id],
        rekeyTo: undefined
      })
    )

    // SWAP STEP 3 (burn)
    params.fee = 0
    transactions.push(
      algosdk.makeApplicationNoOpTxnFromObject({
        from: user.address,
        appIndex: this.appId,
        suggestedParams: params,
        appArgs: [
          TEXT_ENCODER.encode(LENDING_POOL_ROUTER_INTERFACE_STRINGS.swap_step_3)
        ],
        accounts: [],
        foreignApps: [this.market1AppId, this.market2AppId, this.market1.managerAppId],
        foreignAssets: [this.market1.underlyingAssetId, this.market1.bAssetId, this.market2.underlyingAssetId, this.market2.bAssetId],
        rekeyTo: undefined
      })
    )

    return assignGroupID(transactions)
  }
}
